// a detented rotary control: drag around it, scroll it, or use the arrow keys. used for the mode knob

export interface Rotary {
  set(index: number, fire?: boolean): void;
}

const SPAN = 270;
const START = -135;

export function rotary(element: HTMLElement, count: number, onChange: (index: number) => void, value = 0): Rotary {
  let index = value;
  const angle = (at: number) => START + (count > 1 ? (SPAN * at) / (count - 1) : 0);

  function set(next: number, fire = true): void {
    const clamped = Math.max(0, Math.min(count - 1, next));
    const changed = clamped !== index;
    index = clamped;
    element.style.setProperty("--rot", `${angle(index)}deg`);
    element.setAttribute("aria-valuenow", String(index));
    if (fire && changed) onChange(index);
  }

  element.setAttribute("role", "slider");
  element.setAttribute("aria-valuemin", "0");
  element.setAttribute("aria-valuemax", String(count - 1));
  if (!element.hasAttribute("tabindex")) element.tabIndex = 0;

  element.addEventListener("pointerdown", (event) => {
    event.preventDefault();
    element.setPointerCapture(event.pointerId);
    const move = (moved: PointerEvent) => {
      const box = element.getBoundingClientRect();
      let degrees = (Math.atan2(moved.clientX - (box.left + box.width / 2), -(moved.clientY - (box.top + box.height / 2))) * 180) / Math.PI;
      degrees = Math.max(START, Math.min(START + SPAN, degrees));
      set(Math.round(((degrees - START) / SPAN) * (count - 1)));
    };
    const up = () => {
      element.removeEventListener("pointermove", move);
      element.removeEventListener("pointerup", up);
      element.removeEventListener("pointercancel", up);
    };
    element.addEventListener("pointermove", move);
    element.addEventListener("pointerup", up);
    element.addEventListener("pointercancel", up);
  });
  element.addEventListener("wheel", (event) => {
    event.preventDefault();
    set(index + (event.deltaY > 0 ? 1 : -1));
  }, { passive: false });
  element.addEventListener("keydown", (event) => {
    if (event.key === "ArrowRight" || event.key === "ArrowDown") set(index + 1);
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") set(index - 1);
    else return;
    event.preventDefault();
  });

  set(value, false);
  return { set };
}
